import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import * as React from "react";
import {
  Datagrid,
  List,
  TextField,
  TopToolbar,
  useGetOne,
  useListContext,
  useRefresh,
} from "react-admin";
import useNotify from "../unit/useNotify";

import { CloseOutlined } from "@mui/icons-material";
import PostPagination from "../unit/PostPagination";

import sendRequest from "../dataProvider/sendRequest";
import CustomButtonCancel from "../unit/CustomButtonCancel";
import CustomButtonSubmit from "../unit/CustomButtonSubmit";
import CustomDateField from "../unit/CustomDateField";
import CustomExportButton from "../unit/CustomExportButton";
import CustomLoading from "../unit/CustomLoading";

import { hmLightTheme } from "../themes/hmTheme";
import CampaignsListAside from "../campaigns/CampaignsListAside";
import ApplicationLinkField from "../applications/ApplicationLinkField";
import CustomerIdLinkIdField from "../customers/CustomerIdLinkIdField";
import CustomerIdLinkUserIdField from "../customers/CustomerIdLinkUserIdField";
import CampaignsApplicationsFilters from "./vendorsApplicationsFilters";
import CustomFilterButton from "../unit/CustomFilterButton";
import HmLoading from "../unit/HmLoading";
import NoResults from "../unit/NoResults";
import NoPermission from "../unit/NoPermission";
import { downloadCSV } from "react-admin";
import formatDate from "../unit/formatDate";
import getFiltersFromURL from "../unit/getFiltersFromURL";
import PartnersListAside from "../partners/PartnersListAside";
import CustomAmountField from "../unit/CustomAmountField";
import employersLoansFilters from "./vendorsLoansFilters";
import LoanNumLinkField from "../loans/LoanNumLinkField";
import vendorsLoansFilters from "./vendorsLoansFilters";
import CustomNoResults from "../unit/CustomNoResults";
import PartnersCustomLoading from "../partners/PartnersCustomLoading";
const VendorsListActions = ({ ...props }) => {
  return (
    <>
      <TopToolbar sx={{ width: "100%" }}>
        <CustomFilterButton />
        <CustomExportButton />
      </TopToolbar>
    </>
  );
};
const Empty = () => {
  const { total } = useListContext();

  if (total == 0) {
    return <Typography sx={{ p: 5 }}>No Datas ***</Typography>;
  }

  return (
    <Box textAlign="center" m={1}>
      <Typography variant="h4" paragraph>
        No products available{" "}
      </Typography>
      <Typography variant="body1">Create one or import from a file</Typography>
    </Box>
  );
};

const vendorsLoansTypeExporter = (records: any) => {
  let data = [];
  let title = {};

  data = records.map((record: any) => {
    return {
      sLoanNum: record?.sLoanNum ? record.sLoanNum : "",
      dOriginalNoteDate: record?.dOriginalNoteDate
        ? formatDate(record.dOriginalNoteDate, "YYYY-MM-DD")
        : "",
      iApplicationId: record?.iApplicationId ? record.iApplicationId : "",
      iUserId: record?.iUserId ? record.iUserId : "",
      sProductName: record?.sProductName ? record.sProductName : "",
      sOriginalNoteAmount: record?.sOriginalNoteAmount
        ? record.sOriginalNoteAmount
        : "",
      sActivePrincipalBalance: record?.sActivePrincipalBalance
        ? record.sActivePrincipalBalance
        : "",
      sStatus: record?.sStatus ? record.sStatus : "",
      vendor_name: record?.vendor_name ? record.vendor_name : "",
    };
  });
  title = {
    sLoanNum: "Loan #",
    dOriginalNoteDate: "Note Date",
    iApplicationId: "App #",
    iUserId: "User #",
    sProductName: "Product",
    sOriginalNoteAmount: "Original Principal Amount",
    sActivePrincipalBalance: "Remaining Principal Amount",
    sStatus: "Status",
    vendor_name: "Referral",
  };
  return {
    title: title,
    data: data,
  };
};
const CustomExporter = (records: any) => {
  const filterData = vendorsLoansTypeExporter(records);

  let csvTitle = filterData.title;
  let data = filterData.data;
  const currentFilters = getFiltersFromURL();
  const filterString = Object.entries(currentFilters)
    .map(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        return `[${getFieldTitle(key)} ${getFieldValueType(
          key
        )} "${getFieldValue(key, value)}"]`;
      }

      return "";
    })
    .filter(Boolean)
    .join("");
  const csvData = [{ ...csvTitle }, ...data];
  const csvDataStr = csvData.map((row) => Object.values(row).join(","));

  if (filterString) {
    csvDataStr.push("");
    csvDataStr.push("Filters Applied:" + filterString);
  }

  downloadCSV(csvDataStr.join("\n"), "Vendors_Loans_List");
};

function getFieldTitle(field: string) {
  const titleMap: Record<string, string> = {
    iId: "App #",
    iApplicationId: "App #",
    sLoanNum: "Loan #",
    iUserId: "User #",
    application_submitted: "Application Submitted",
    limitNoteDate: "Note Date",
    product: "Product",
    sProductName: "Product",
    originalnoteamount: "Original Principal Amount",
    activeprincipalbalance: "Remaining Principal Amount",
    curStatus: "Status",
    vendor_name: "Referral",
  };

  return titleMap[field] || field;
}

function getFieldValueType(field: string) {
  const typeMap: Record<string, string> = {
    iId: "contains",
    iApplicationId: "contains",
    sLoanNum: "contains",
    iUserId: "contains",
    application_submitted: "is",
    limitNoteDate: "is",
    product: "is",
    sProductName: "is",
    originalnoteamount: "in",
    activeprincipalbalance: "in",
    curStatus: "is",
    vendor_name: "contains",
  };

  return typeMap[field] || "is";
}

function getFieldValue(field: string, value: any) {
  if (field == "originalnoteamount" || field == "activeprincipalbalance") {
    if (value == "1") {
      return "<$1000";
    } else if (value == "2") {
      return "$1000-$5000";
    } else if (value == "3") {
      return "$5000-$10,000";
    } else if (value == "4") {
      return "$10,000-$20,000";
    } else if (value == "5") {
      return ">$20,000";
    } else if (value == "0") {
      return "0";
    } else if (value == ">0") {
      return "> 0";
    } else if (value == "<0") {
      return "< 0";
    }
  }

  if (field == "application_submitted" || field == "limitNoteDate") {
    if (value == "current") {
      return "Current Year";
    } else if (value == "last1") {
      return "Last Year";
    } else if (value == "last2_3") {
      return "Last 2-3 Years";
    } else if (value == "last3_5") {
      return "Last 3-5 Years";
    } else if (value == "last5") {
      return "> 5 Years Ago";
    }
  }

  return value;
}

const vendorsLoansList = () => {
  const refresh = useRefresh();
  const [open, setOpen] = React.useState(false);
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const [record, setRecord] = React.useState<{ total: number } | null>(null);
  const permissions =
    localStorage.getItem("bbaccess")?.toString().split(",") || [];
  if (!permissions.includes("personal_campaigns")) {
    return <NoPermission />;
  }
  const { data, isLoading, error } = useGetOne("total/campaign", { id: 1 });
  React.useEffect(() => {
    setRecord(data);
  }, [data]);

  const total = record ? record.total : 0;

  const CustomRowStyle = (record: any) => {
    if (!record) return {};

    if (record?.sStatus != "Declined" && record?.sStatus != "Expired") {
      return {
        backgroundColor: "#fff",
        "&.MuiTableRow-hover:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      };
    } else {
      return {
        backgroundColor: "#f5f5f5",
        "& td.MuiTableCell-root": {
          color: "gray",
        },
        "& td.MuiTableCell-root span": {
          color: "gray",
        },
        "& td.MuiTableCell-root a": {
          color: "gray",
        },
        "& td.MuiTableCell-root p": {
          color: "gray",
        },
      };
    }
  };
  return (
    <>
      <List
        actions={<VendorsListActions />}
        filters={vendorsLoansFilters}
        aside={
          <PartnersListAside totalData={total} selected="vendorsToLoans" />
        }
        sort={{ field: "created_at", order: "DESC" }}
        exporter={CustomExporter}
        sx={{
          width: "100%",
          borderRadius: "0px",
          "& .MuiCard-root": {
            borderRadius: "0px",
          },
          "& .RaList-actions form": {
            flex: "0 1 350%",
          },
          "& .RaList-actions form .filter-field .MuiInputBase-input": {
            width: "230px",
          },
          "& .RaList-actions .RaFilterForm-filterFormInput:first-child .MuiInputBase-root::before":
            {
              display: "none",
            },
          "& .RaList-actions .filter-field:first-child .MuiFormControl-root": {
            marginLeft: 0,
          },
        }}
        empty={false}
        emptyWhileLoading={false}
        loading={<HmLoading />}
        pagination={<PostPagination />}
        perPage={25}
      >
        <Datagrid
          optimized
          rowClick={false}
          size="small"
          id="table-campaigns"
          className="hm-list-datagrid"
          empty={false}
          sx={{
            ".MuiTableCell-root": {
              padding: "6px",
            },
            ".RaDatagrid-headerCell:first-child": {
              borderTopLeftRadius: "0px",
            },
            ".RaDatagrid-headerCell:last-child": {
              borderTopRightRadius: "0px",
            },
            overflowY: "auto",
            "& .column-groups": {
              md: { display: "none" },
              lg: { display: "table-cell" },
            },
            "& .Mui-active": {
              color: "gray",
            },
            "& .MuiTableSortLabel-root": {
              padding: "5px",
            },
            "& .RaDatagrid-headerCell": {
              color: "gray",
              fontSize: "1rem",
              backgroundColor: "#edf4fb",
              wordWrap: "break-word",
            },
            "& .MuiTableCell-paddingCheckbox": {
              display: "none",
            },
            "& .MuiTableCell-root.column-id": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
            },
            "& .MuiTableCell-root.column-sUrl": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-sName": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-created_at": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-column-action": {
              minWidth: "250px",
              width: "250px",
              maxWidth: "250px",
            },
          }}
        >
          {/* <TextField source="sLoanNum" label="Loan #" sortable={true} /> */}
          <LoanNumLinkField label="Loan #" sortBy="loannum" source="sLoanNum" />
          <CustomDateField
            source="dOriginalNoteDate"
            label="Note Date"
            onlyShowDate={true}
            sortable={true}
            sortBy="notedate"
            cellClassName="dateText"
          />
          <ApplicationLinkField
            source="iApplicationId"
            label="App #"
            sortBy="iId"
            sortable={true}
          />
          <CustomerIdLinkUserIdField
            source="iUserId"
            label="User #"
            sortBy={"iUserId"}
            sortable={true}
          />

          <TextField source="sProductName" label="Product" sortable={true} />
          <CustomAmountField
            source="sOriginalNoteAmount"
            label="Original Principal Amount"
            sortBy="originalnoteamount"
            cellClassName="amountText"
          />
          <CustomAmountField
            source="sActivePrincipalBalance"
            label="Remaining Principal Amount"
            sortBy="activeprincipalbalance"
            cellClassName="amountText"
          />
          <TextField
            source="sStatus"
            label="Status"
            sortable={true}
            sortBy="status"
          />
          <TextField source="vendor_name" label="Referral" sortable={true} />
        </Datagrid>
        <PartnersCustomLoading checkedField="vendor_name" />
      </List>
    </>
  );
};

export default vendorsLoansList;
